import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Target, LineChart, MapPin,
  ShieldCheck, Radar, Zap, ScanLine,
} from "lucide-react";
import { Header } from "@/components/Header";
import { useI18n } from "@/lib/i18n";

const Capability = ({ icon: Icon, title, body, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.5, delay: index * 0.08 }}
    className="impact-card p-7 md:p-8 hover:-translate-y-1 hover:shadow-[0_18px_50px_-20px_rgba(76,166,255,0.35)] transition-all duration-300 impact-glow-border"
    data-testid="capability-card"
  >
    <div className="flex items-center justify-between mb-6">
      <div className="w-11 h-11 rounded-lg bg-[color:var(--impact-navy-900)] text-[color:var(--impact-cyan-400)] grid place-items-center relative overflow-hidden">
        <Icon size={20} strokeWidth={1.8} />
        <span className="absolute inset-0 bg-gradient-to-tr from-[color:var(--impact-cyan-500)]/20 to-transparent" />
      </div>
      <span className="font-mono eyebrow text-[10px]">0{index + 1}</span>
    </div>
    <div className="font-display font-bold text-xl mb-2">{title}</div>
    <p className="text-[color:var(--impact-ink-2)] leading-relaxed text-[15px]">{body}</p>
  </motion.div>
);

/* Radar sweep visual — pure CSS/SVG, no images */
const RadarVisual = () => (
  <div className="relative w-full aspect-square max-w-[420px] mx-auto">
    <div className="absolute inset-0 rounded-full border border-white/10" />
    <div className="absolute inset-[10%] rounded-full border border-white/10" />
    <div className="absolute inset-[22%] rounded-full border border-white/10" />
    <div className="absolute inset-[35%] rounded-full border border-white/15" />
    <div className="absolute inset-[48%] rounded-full border border-white/20" />
    {/* sweep */}
    <div
      className="absolute inset-0 rounded-full"
      style={{
        background: "conic-gradient(from 0deg, transparent 0deg, rgba(76,166,255,0.55) 40deg, transparent 60deg)",
        animation: "spin 6s linear infinite",
        maskImage: "radial-gradient(circle, black 60%, transparent 62%)",
        WebkitMaskImage: "radial-gradient(circle, black 60%, transparent 62%)",
      }}
    />
    {/* crosshair */}
    <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
    <div className="absolute inset-y-0 left-1/2 w-px bg-white/10" />
    {/* nodes */}
    {[
      { top: "22%", left: "62%" },
      { top: "58%", left: "30%" },
      { top: "72%", left: "70%" },
      { top: "38%", left: "80%" },
    ].map((p, i) => (
      <div key={i} className="absolute w-2 h-2 rounded-full bg-[color:var(--impact-cyan-400)] shadow-[0_0_18px_rgba(76,166,255,0.7)]" style={p}>
        <span className="pulse-ring" />
      </div>
    ))}
    {/* center core */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow-[0_0_20px_rgba(255,255,255,0.6)]" />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

/* Metric card floating on hero */
const MetricCard = () => (
  <motion.div
    initial={{ opacity: 0, x: 40 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.4, duration: 0.7 }}
    className="impact-card-glass impact-glow-border p-5 w-[300px] absolute right-0 top-8 hidden xl:block"
  >
    <div className="flex items-center justify-between mb-3">
      <span className="eyebrow eyebrow-cyan">Aperçu</span>
      <span className="font-mono text-[10px] text-white/50">v1.1</span>
    </div>
    <div className="flex items-baseline gap-2">
      <span className="font-display font-black text-4xl text-white">72</span>
      <span className="text-xs text-white/50">/ 100</span>
    </div>
    <div className="mt-4 space-y-2">
      {[
        ["Site & technique", 88, "cyan"],
        ["Conversion", 43, "gold"],
        ["Présence locale", 25, "high"],
        ["Prép. payante", 100, "cyan"],
      ].map(([l, v, c]) => (
        <div key={l} className="flex items-center gap-3">
          <div className="text-[11px] w-24 text-white/60 font-mono">{l}</div>
          <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${v}%`,
                background: c === "cyan" ? "var(--impact-cyan-500)"
                  : c === "gold" ? "var(--impact-gold-500)"
                  : "#ff5577",
              }}
            />
          </div>
          <div className="text-[11px] font-mono text-white w-8 text-right">{v}</div>
        </div>
      ))}
    </div>
  </motion.div>
);

const LandingPage = () => {
  const { t } = useI18n();
  return (
    <div data-testid="landing-page" className="min-h-screen">
      <Header variant="dark" />

      {/* ============ HERO — dark tech ============ */}
      <section className="impact-dark impact-dark-grid impact-scanlines">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24 md:pb-36 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr,1fr] gap-12 items-center relative">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10"
            >
              <div className="impact-pill impact-pill-dark mb-8" data-testid="hero-badge">
                <Sparkles size={12} /> {t("landing.badge")}
              </div>

              <h1
                className="font-display font-black tracking-tighter text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] mb-8 text-white"
                data-testid="hero-headline"
              >
                {t("landing.headline")}
              </h1>

              <p className="text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed mb-10">
                {t("landing.subheadline")}
              </p>

              <div className="flex flex-wrap items-center gap-4">
                <Link to="/audit" data-testid="hero-cta-btn" className="btn-primary btn-brand text-[15px]">
                  {t("landing.primaryCta")} <ArrowRight size={18} />
                </Link>
                <span className="text-sm text-white/60 font-mono" data-testid="hero-reassurance">
                  {t("landing.reassurance")}
                </span>
              </div>

              {/* mini tagline echo of logo */}
              <div className="mt-10 flex items-center gap-6 text-[11px] font-mono uppercase tracking-[0.24em] text-white/40">
                <span>Stratégie</span>
                <span className="w-1 h-1 rounded-full bg-[color:var(--impact-gold-500)]" />
                <span>Visibilité</span>
                <span className="w-1 h-1 rounded-full bg-[color:var(--impact-gold-500)]" />
                <span>Croissance</span>
              </div>
            </motion.div>

            <div className="relative">
              <MetricCard />
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                <RadarVisual />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="border-t border-b border-[color:var(--impact-border)] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-6">
          <div className="eyebrow eyebrow-gold">Impact Digital · Agence performance</div>
          <div className="flex items-center gap-6 md:gap-10 text-sm text-[color:var(--impact-ink-2)]">
            <span className="flex items-center gap-2"><Zap size={14} className="text-[color:var(--impact-cyan-500)]" /> Google Ads</span>
            <span className="flex items-center gap-2"><Radar size={14} className="text-[color:var(--impact-cyan-500)]" /> Conversion</span>
            <span className="flex items-center gap-2"><ScanLine size={14} className="text-[color:var(--impact-cyan-500)]" /> Sites Web</span>
          </div>
        </div>
      </section>

      {/* ============ CAPABILITIES ============ */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="eyebrow eyebrow-cyan mb-3">01 — {t("landing.sectionCapabilities")}</div>
            <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl max-w-2xl">
              {t("landing.trustTitle")}
            </h2>
          </div>
          <p className="max-w-md text-[color:var(--impact-ink-2)]">{t("landing.trustBody")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Capability icon={Target} title={t("landing.cap1Title")} body={t("landing.cap1Body")} index={0} />
          <Capability icon={LineChart} title={t("landing.cap2Title")} body={t("landing.cap2Body")} index={1} />
          <Capability icon={ShieldCheck} title={t("landing.cap3Title")} body={t("landing.cap3Body")} index={2} />
          <Capability icon={MapPin} title={t("landing.cap4Title")} body={t("landing.cap4Body")} index={3} />
        </div>
      </section>

      {/* ============ CTA — dark ============ */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <div className="impact-dark impact-dark-grid rounded-[24px] p-10 md:p-16 relative overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-10 items-center relative z-10">
            <div className="max-w-2xl">
              <div className="eyebrow eyebrow-gold mb-3">Impact Audit AI</div>
              <h3 className="font-display font-bold tracking-tight text-3xl md:text-4xl text-white mb-3">
                {t("landing.headline")}
              </h3>
              <p className="text-white/60 text-[15px]">{t("landing.reassurance")}</p>
            </div>
            <Link
              to="/audit"
              data-testid="cta-secondary-btn"
              className="btn-primary btn-brand text-[15px] whitespace-nowrap"
            >
              {t("landing.primaryCta")} <ArrowRight size={18} />
            </Link>
          </div>
          <div className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full border border-white/10 pointer-events-none" />
          <div className="absolute -right-40 -bottom-40 w-[560px] h-[560px] rounded-full border border-white/5 pointer-events-none" />
        </div>
      </section>

      <footer className="border-t border-[color:var(--impact-border)] py-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-4 text-sm text-[color:var(--impact-ink-2)]">
          <div className="flex items-center gap-3">
            <img src="/impact-digital-logo.png" alt="" className="h-6 w-auto opacity-70" />
            <span>© {new Date().getFullYear()} Impact Digital</span>
          </div>
          <a href="mailto:infos@agenceimpactdigital.ca" className="animated-underline hover:text-[color:var(--impact-ink)]">
            infos@agenceimpactdigital.ca
          </a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
